import type { BudgetStatus, CurrencyTotals, MonthlySummary } from "@/api/types";

/** Variação percentual inteira; `null` quando não há base (mês anterior zerado ou ausente) — nunca ∞%. */
function delta(current: number, previous: number | undefined): number | null {
  if (!previous) return null;
  return Math.round(((current - previous) / previous) * 100);
}

export function monthComparison(current: CurrencyTotals, previous: CurrencyTotals | undefined) {
  return {
    income: { valueMinor: current.incomeMinor, deltaPct: delta(current.incomeMinor, previous?.incomeMinor) },
    expense: {
      valueMinor: current.expenseMinor,
      deltaPct: delta(current.expenseMinor, previous?.expenseMinor),
    },
    netMinor: current.netMinor,
  };
}

export type CategoryColor = `cat-${1 | 2 | 3 | 4 | 5}` | "cat-other";
export interface Slice {
  key: string;
  name: string;
  valueMinor: number;
  color: CategoryColor;
}

/**
 * Fatias do gráfico de despesas do mês: as `n` maiores categorias (cor fixa pela posição), o resto em "Outras" e,
 * se o total do mês (`totalExpenseMinor`, do fluxo de caixa) tiver despesa sem categoria, a fatia "Sem categoria".
 * As fatias somam `totalExpenseMinor`.
 */
export function topCategories(
  summary: Pick<MonthlySummary, "byCategory">,
  totalExpenseMinor: number,
  n = 4,
): Slice[] {
  const expenses = summary.byCategory
    .filter((c) => c.kind === "expense" && c.totalMinor > 0)
    .sort((a, b) => b.totalMinor - a.totalMinor);
  const slices: Slice[] = expenses.slice(0, n).map((c, i) => ({
    key: c.categoryId,
    name: c.name,
    valueMinor: c.totalMinor,
    color: `cat-${(i + 1) as 1 | 2 | 3 | 4}`,
  }));
  const others = expenses.slice(n).reduce((s, c) => s + c.totalMinor, 0);
  if (others > 0) slices.push({ key: "__others", name: "Outras", valueMinor: others, color: "cat-other" });
  const categorized = expenses.reduce((s, c) => s + c.totalMinor, 0);
  const uncategorized = totalExpenseMinor - categorized;
  if (uncategorized > 0)
    slices.push({ key: "__none", name: "Sem categoria", valueMinor: uncategorized, color: "cat-5" });
  return slices;
}

export type BudgetLevel = "ok" | "warning" | "over";

/** Nível de um orçamento pelo percentual usado: ≥ 80% atenção, > 100% estourado. */
export const budgetLevel = (percentUsed: number): BudgetLevel =>
  percentUsed > 100 ? "over" : percentUsed >= 80 ? "warning" : "ok";

/** Orçamentos com nível, do mais crítico para o menos. */
export function budgetAlerts(status: BudgetStatus[]) {
  return status
    .map((b) => ({ ...b, level: budgetLevel(b.percentUsed) }))
    .sort((a, b) => b.percentUsed - a.percentUsed);
}
